import { makeObservable } from 'mobx'

import { SaveLocationsDocument } from '~/client/graph/operations/generated/Locations.generated'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import ILocationDto from '~/client/src/shared/types/ILocationDto'

import Level from '../../models/LocationObjects/Level'
import { sortLevels } from '../../utils/sortingFunctions'
import EventsStore from '../EventStore/Events.store'
import BuildingsStore from './Buildings.store'
import GraphExecutorStore from './GraphExecutor.store'
import LocationStoreBase from './LocationBase.store'

export default class LevelsStore extends LocationStoreBase<Level> {
  protected saveMutation = SaveLocationsDocument
  protected graphName = 'locations'
  protected defaultIconName = MapViewLocationIcon.Level

  public constructor(
    protected readonly eventsStore: EventsStore,
    protected readonly graphExecutorStore: GraphExecutorStore,
    private readonly buildingsStore: BuildingsStore,
  ) {
    super(eventsStore, graphExecutorStore)
    makeObservable(this)
  }

  public get list() {
    const allLevels = this.listWithDeletedItems.filter(
      entry => !entry.isDeleted,
    )

    // Under a building by the parent's id, the same test the building groups below use:
    // parentType can be stale ("Location", or an old kind), and a level judged by it would
    // be listed both under its building and among the levels without one.
    const buildingIds = new Set(this.buildingsStore.list.map(({ id }) => id))
    const levelsWithoutBuildingParent = allLevels
      .filter(lvl => !buildingIds.has(lvl.parent?.parentId))
      .sort((a, b) => sortLevels(b, a))

    const levelsSortedByBuildings = this.buildingsStore.list.reduce(
      (list, buildingDto) => {
        const levelsByBuilding = allLevels.filter(lvl =>
          lvl.isParent(buildingDto),
        )
        const sortedLevels =
          buildingDto.sortLevelsByCustomOrder(levelsByBuilding)

        list.push(...sortedLevels)

        return list
      },
      [] as Level[],
    )

    return levelsSortedByBuildings.concat(levelsWithoutBuildingParent)
  }

  public get byId() {
    const { levels } = this.eventsStore.appState
    return levels
  }

  protected fromDto(dto: ILocationDto): Level {
    return Level.fromDto(dto)
  }
}
